import type { Contract } from '@beacon/core';
import type {
  ContractCreateInput,
  ContractUpdateInput,
} from '@beacon/core/validation';

import type { BeaconClient } from '../client.js';

export interface ContractListResponse {
  contracts: Contract[];
}

export interface ContractAuditEntry {
  id: string;
  contractId: string;
  userId: string | null;
  userName: string | null;
  userEmail: string | null;
  action: 'create' | 'update';
  /** `{[field]: [before, after]}` — fields with no change are omitted. */
  changes: Record<string, [unknown, unknown]>;
  createdAt: string;
}

export interface ContractAuditListResponse {
  entries: ContractAuditEntry[];
}

export type ContractUsage =
  | {
      type: 'sla';
      contractId: string;
      periodStartDate: string;
      periodEndDate: string;
      usedHours: number;
      allocatedHours: number;
      remainingHours: number;
      percent: number;
    }
  | {
      type: 'bau';
      contractId: string;
      usedHours: number;
      budgetHours: number;
      remainingHours: number;
      percent: number;
    }
  | { type: 'internal'; contractId: string };

export const contractsApi = {
  list(client: BeaconClient): Promise<ContractListResponse> {
    return client.get<ContractListResponse>('/api/contracts');
  },

  getById(client: BeaconClient, id: string): Promise<Contract> {
    return client.get<Contract>(`/api/contracts/${encodeURIComponent(id)}`);
  },

  create(
    client: BeaconClient,
    input: ContractCreateInput
  ): Promise<{ id: string }> {
    return client.post<{ id: string }>('/api/contracts', input);
  },

  update(
    client: BeaconClient,
    id: string,
    input: ContractUpdateInput
  ): Promise<Contract> {
    return client.patch<Contract>(
      `/api/contracts/${encodeURIComponent(id)}`,
      input
    );
  },

  getUsage(client: BeaconClient, id: string): Promise<ContractUsage> {
    return client.get<ContractUsage>(
      `/api/contracts/${encodeURIComponent(id)}/usage`
    );
  },

  getAudit(
    client: BeaconClient,
    id: string
  ): Promise<ContractAuditListResponse> {
    return client.get<ContractAuditListResponse>(
      `/api/contracts/${encodeURIComponent(id)}/audit`
    );
  },
};
